import { Dispatch, FC, SetStateAction } from 'react';
import { Box, Flex, StyledFormSelect, StyledInput, StyledDatePicker, WrappedQuery } from '.';
import { formatSelectValue, isMobile } from '../../utils';

type FilterOption = {
  label: string;
  value: string;
  isText?: boolean;
  isNumber?: boolean;
  isDate?: boolean;
  isDateRange?: boolean;
  options?: { label: string; value: string | boolean | number }[];
  query?: {
    query: any;
    result: string;
    resultOptionFunction?: (data: any) => { label: string; value: string };
    variables: any;
    notSearchable?: boolean; // default is searchable
    searchText?: string;
    setSearchText?: Dispatch<SetStateAction<string>>;
  };
  startDate?: string;
  endDate?: string;
};

type Option = { label: string; value: string | boolean | number };

const buildQueryOptions = (filterOption: FilterOption, data: any, loading: boolean, error: any): Option[] => {
  if (loading || error || !filterOption.query) {
    return [];
  }

  if (data[filterOption.query.result].edges) {
    return data[filterOption.query.result].edges.map((item: any) => {
      if (filterOption.query && filterOption.query.resultOptionFunction) {
        return filterOption.query.resultOptionFunction(item.node);
      }
      return formatSelectValue({
        ...((item.node.firstName || item.node.lastName) && {
          label: `${item.node.firstName} ${item.node.lastName}`,
        }),
        ...(item.node.localMunicipalityShorthand && {
          label: item.node.localMunicipalityShorthand,
        }),
        ...(item.node.name && { label: item.node.name }),
        value: item.node.id,
      });
    });
  }

  return data[filterOption.query.result].map((item: any) => {
    if (filterOption.query && filterOption.query.resultOptionFunction) {
      return filterOption.query.resultOptionFunction(item);
    }
    return formatSelectValue({
      ...((item.firstName || item.lastName) && {
        label: `${item.firstName} ${item.lastName}`,
      }),
      ...(item.localMunicipalityShorthand && {
        label: item.localMunicipalityShorthand,
      }),
      ...(item.name && { label: item.name }),
      ...(item.value && { label: item.value }),
      ...(item.institution && { label: item.institution.name }),
      ...(item.institution && { value: item.institution.id }),
      ...(!item.institution && {
        value: item.id,
      }),
    });
  });
};

type ChangeHandlerArgs = {
  value: string | Date | number | null;
};

// Opt-in only. Screens that seed a filter before the panel is ever opened (the My Cases landing
// filters) need the selects controlled so the seeded value is visible; every other screen keeps
// react-select's own internal state by leaving the value undefined.
const selectedOption = (
  controlled: boolean | undefined,
  options: Option[] | undefined,
  value: any,
  valueLabels?: Record<string, string>
) => {
  if (!controlled) {
    return undefined;
  }
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const match = (options || []).find(option => option.value === value);
  if (match) {
    return match;
  }
  // Options come from paginated queries, so a seeded value can sit beyond the first page.
  // The caller supplies the label for values it seeds itself.
  return valueLabels && valueLabels[value] ? { label: valueLabels[value], value } : null;
};

type Props = {
  changeHandler: (args: ChangeHandlerArgs, stateKey?: string) => void;
  filterOptions: FilterOption[];
  filterState: SetStateAction<any>;
  searchName?: string;
  setSearchName?: (data: string) => void;
  hiddenField?: string;
  changeHiddenField?: (field: string, value: string) => void;
  fieldToHide?: string;
  controlledValues?: boolean;
  valueLabels?: Record<string, string>;
};

/**
 * This component is intended to be pure and extensible for use
 * in all the instances where we need to add filters
 * @param param0
 */

const CollapsibleFilters: FC<Props> = ({
  changeHandler,
  filterOptions,
  filterState,
  searchName,
  setSearchName,
  hiddenField,
  changeHiddenField,
  fieldToHide,
  controlledValues,
  valueLabels,
}) => {
  const filterType = filterState.filterType;

  return (
    <Flex size="1" direction={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'flex-left' : 'center'} px={4}>
      {filterOptions.map((filterOption: FilterOption, index) => {
        if (hiddenField === filterOption.value || filterOption.value !== fieldToHide) {
          return (
            <Flex key={`filter-${index}`} size="0.25" direction="column" mr={4}>
              <label>{filterOption.label}</label>
              {filterOption && filterOption.query && (
                <Flex size="0.25" direction="row" marginRight="10px">
                  <WrappedQuery query={filterOption.query.query} variables={filterOption.query.variables}>
                    {({ loading, error, data, fetchMore }) => (
                      <StyledFormSelect
                        aria-label="Refine"
                        autoFocus={true}
                        value={selectedOption(
                          controlledValues,
                          buildQueryOptions(filterOption, data, loading, error),
                          filterState[filterOption.value],
                          valueLabels
                        )}
                        isSearchable={
                          !!filterOption.query && !!filterOption.query.notSearchable
                            ? false
                            : (!!filterOption.query && !!filterOption.query.setSearchText) || !!setSearchName
                        }
                        inputValue={
                          !!filterOption.query && !!filterOption.query.notSearchable
                            ? undefined
                            : !!filterOption.query && filterOption.query.searchText !== undefined
                            ? filterOption.query.searchText
                            : searchName
                        }
                        isClearable={true}
                        name="refine"
                        mb="0"
                        onChange={option => {
                          changeHandler({ value: option && option.value }, filterOption.value);
                          if (changeHiddenField && option && filterOption) {
                            changeHiddenField(filterOption.value, option.label);
                          }
                        }}
                        options={buildQueryOptions(filterOption, data, loading, error)}
                        placeholder="Select option"
                        setSearchName={
                          !!filterOption.query && !!filterOption.query.notSearchable
                            ? undefined
                            : !!filterOption.query && !!filterOption.query.setSearchText
                            ? filterOption.query.setSearchText
                            : setSearchName
                        }
                        scrollHandler={async () => {
                          if (filterOption.query) {
                            // call the next page of the circuits query
                            return (
                              data[filterOption.query.result] &&
                              data[filterOption.query.result].pageInfo &&
                              data[filterOption.query.result].pageInfo.hasNextPage &&
                              fetchMore({
                                variables: {
                                  input: {
                                    ...(filterOption.query.variables &&
                                      filterOption.query.variables.input && {
                                        ...filterOption.query.variables.input,
                                      }),
                                    after:
                                      data[filterOption.query.result] &&
                                      data[filterOption.query.result].pageInfo &&
                                      data[filterOption.query.result].pageInfo.endCursor,
                                  },
                                },
                                updateQuery: (previousResult, { fetchMoreResult }) => {
                                  if (filterOption.query) {
                                    const newResults = fetchMoreResult[filterOption.query.result].edges;

                                    return {
                                      [filterOption.query.result]: {
                                        aggregate: fetchMoreResult[filterOption.query.result].aggregate,
                                        pageInfo: fetchMoreResult[filterOption.query.result].pageInfo,
                                        edges: [...previousResult[filterOption.query.result].edges, ...newResults],
                                        __typename: fetchMoreResult[filterOption.query.result].__typename,
                                      },
                                    };
                                  }
                                  return;
                                },
                              })
                            );
                          }
                          return false;
                        }}
                      />
                    )}
                  </WrappedQuery>
                </Flex>
              )}
              {filterOption && filterOption.options && (
                <Flex size="0.25" direction="row" marginRight="10px">
                  <StyledFormSelect
                    isClearable={true}
                    name="refine"
                    aria-label="Refine"
                    value={selectedOption(controlledValues, filterOption.options, filterState[filterOption.value])}
                    onChange={option => changeHandler({ value: option && option.value }, filterOption.value)}
                    options={filterOption.options}
                    placeholder="Select ..."
                    mb="0"
                    isSearchable={false}
                  />
                </Flex>
              )}
              {filterOption && (filterOption.isText || filterOption.isNumber) && (
                <Flex size="0.25" direction="row" marginRight="10px">
                  <Box width="100%">
                    <StyledInput
                      type={filterOption.isNumber ? 'number' : 'text'}
                      id="searchTerm"
                      aria-label="Search"
                      onChange={(event: { target: { value: string | number } }) =>
                        changeHandler({ value: event.target.value }, filterOption.value)
                      }
                      placeholder="Type here..."
                      px={2}
                    />
                  </Box>
                </Flex>
              )}
              {filterOption && filterOption.isDate && (
                <Flex size="0.25" direction="row" marginRight="10px">
                  <Box width="100%">
                    <StyledDatePicker
                      aria-label="Date"
                      name="dateSearch"
                      isSingleDate={true}
                      setStartDate={(startDate: Date | null) => changeHandler({ value: startDate }, filterOption.value)}
                      setEndDate={() => null}
                      startDate={filterState[filterType]}
                      px={0}
                      mb={1}
                      mr={0}
                      ml={0}
                    />
                  </Box>
                </Flex>
              )}
              {filterOption && filterOption.isDateRange && (
                <Flex size="0.25" direction="row" marginRight="10px">
                  <Box width="100%">
                    <StyledDatePicker
                      aria-label="Date Range"
                      name="dateRangeSearch"
                      isSingleDate={false}
                      setStartDate={(startDate: Date | null) =>
                        changeHandler({ value: startDate }, filterOption.startDate)
                      }
                      setEndDate={(endDate: Date | null) => changeHandler({ value: endDate }, filterOption.endDate)}
                      startDate={filterOption && filterOption.startDate && filterState[filterOption.startDate]}
                      endDate={filterOption && filterOption.endDate && filterState[filterOption.endDate]}
                      px={0}
                      mx={0}
                      mb={1}
                      mt={'-2px'}
                    />
                  </Box>
                </Flex>
              )}
            </Flex>
          );
        }
        return null;
      })}
    </Flex>
  );
};

export default CollapsibleFilters;
